import { FC } from "react";

import Box from "@mui/material/Box";

import { BaseStep1LayoutProps } from "./BaseStep1LayoutProps";
import { ONE_COLUMN_MAX_WIDTH } from "./BaseStep1OneColumnLayout";

import { FormRegisteredCharityStatement } from "@/canvas/features/FormV2/blocks/shell/FormRegisteredCharityStatement";
import { ThemedZeffyLogo } from "@/canvas/layouts/ThemedBranding/ThemedZeffyLogo";
import { useHasValueChanged } from "@/common/hooks/useHasValueChanged";

type BaseStep1EmbedLayoutProps = {
  showCheckoutFooter?: BaseStep1LayoutProps["showCheckoutFooter"];
  slots: Pick<BaseStep1LayoutProps["slots"], "checkoutFooter" | "order" | "shopCartButton">;
  logoHref: BaseStep1LayoutProps["logoHref"];
  onLogoClick?: BaseStep1LayoutProps["onLogoClick"];
};

export const BaseStep1EmbedLayout: FC<BaseStep1EmbedLayoutProps> = ({
  slots,
  showCheckoutFooter = false,
  logoHref,
  onLogoClick,
}) => {
  const [animateCheckoutFooter, watchedShowCheckoutFooter] = useHasValueChanged(showCheckoutFooter);

  const renderAnimatedCheckoutFooter = () => {
    const hidden = !watchedShowCheckoutFooter && !animateCheckoutFooter;

    return (
      <Box
        data-test="form-v2-checkout-footer"
        position={hidden ? "absolute" : "sticky"}
        bottom={{ xs: 32, md: 40 }}
        marginBlockStart={hidden ? 0 : 2}
        sx={{
          animation: animateCheckoutFooter
            ? watchedShowCheckoutFooter
              ? "fade-slide-in 0.3s ease-in-out both"
              : "fade-slide-out 0.3s ease-in-out both"
            : undefined,
          opacity: hidden ? 0 : undefined,
          height: hidden ? 0 : undefined,
          zIndex: 1000,
          ["@keyframes fade-slide-in"]: {
            from: { translate: "0 20px", opacity: 0 },
            to: { translate: "0 0", opacity: 1 },
          },
          ["@keyframes fade-slide-out"]: {
            from: { translate: "0 0", opacity: 1 },
            to: { translate: "0 20px", opacity: 0 },
          },
        }}
      >
        {slots.checkoutFooter}
      </Box>
    );
  };

  return (
    <Box
      width="100%"
      maxWidth={ONE_COLUMN_MAX_WIDTH}
      marginInline="auto"
      padding={{ xs: 2, sm: 5 }}
      sx={{ boxSizing: "border-box" }}
    >
      {slots.order}
      {renderAnimatedCheckoutFooter()}
      <FormRegisteredCharityStatement />
      <ThemedZeffyLogo
        href={logoHref}
        onClick={onLogoClick}
        sx={{ display: "block", marginInline: "auto", marginBlockStart: 3, width: { md: 66 }, height: { md: 20 } }}
      />
      {slots.shopCartButton && (
        <Box
          sx={{
            position: "fixed",
            top: 24,
            right: 24,
            zIndex: 1001,
          }}
        >
          {slots.shopCartButton}
        </Box>
      )}
    </Box>
  );
};
